import React, { useEffect, useMemo, useRef, useState } from "react";
import { tuitionConfigs, getStudentPaymentSchedule } from "../../data/tuitionConfigs";
import { loadGradeSections } from "../../services/sectionsService";
import { uploadStudentPhoto } from "../../services/studentsService";

export default function StudentForm({ initial = {}, onSubmit, onCancel }) {
  const normalizeDateInput = (v) => {
    if (!v) return "";
    try {
      if (typeof v === "object" && typeof v.toDate === "function") {
        return v.toDate().toISOString().slice(0, 10);
      }
    } catch { void 0; }
    const s = String(v || "").trim();
    if (!s) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const m1 = s.match(/^(\d{4})[/.](\d{1,2})[/.](\d{1,2})$/);
    if (m1) return `${m1[1]}-${String(m1[2]).padStart(2, "0")}-${String(m1[3]).padStart(2, "0")}`;
    const m2 = s.match(/^(\d{1,2})[/.](\d{1,2})[/.](\d{4})$/);
    if (m2) return `${m2[3]}-${String(m2[1]).padStart(2, "0")}-${String(m2[2]).padStart(2, "0")}`;
    const m3 = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (m3) return `${m3[3]}-${String(m3[1]).padStart(2, "0")}-${String(m3[2]).padStart(2, "0")}`;
    return s;
  };

  const [form, setForm] = useState({
    id: initial.id || initial.studentId || "",
    photo: null,
    photoUrl: initial.photoUrl || "",
    lrn: initial.lrn || "",
    currentHouse: initial.currentHouse || "",
    currentStreet: initial.currentStreet || "",
    currentBarangay: initial.currentBarangay || "",
    currentCity: initial.currentCity || initial.address || "",
    currentProvince: initial.currentProvince || "",
    currentCountry: initial.currentCountry || "Philippines",
    currentZip: initial.currentZip || "",
    permanentHouse: initial.permanentHouse || "",
    permanentStreet: initial.permanentStreet || "",
    permanentBarangay: initial.permanentBarangay || "",
    permanentCity: initial.permanentCity || "",
    permanentProvince: initial.permanentProvince || "",
    permanentCountry: initial.permanentCountry || "Philippines",
    permanentZip: initial.permanentZip || "",
    samePermanentAsCurrent: !!initial.samePermanentAsCurrent,
    email: initial.email || "",
    lastname: initial.lastName || initial.lastname || initial.last_name || "",
    firstname: initial.firstName || initial.firstname || initial.first_name || "",
    middlename: initial.middlename || initial.middleName || initial.middle_name || "",
    gradeLevel: initial.gradeLevel || "",
    strand: initial.strand || "",
    section: initial.section || "",
    birthdate: normalizeDateInput(initial.birthdate || initial.birthDate || initial.birth_date),
    gender: initial.gender || "",
    birthPlaceCity: initial.birthPlaceCity || "",
    motherTongue: initial.motherTongue || "",
    address: initial.address || "",
    contact: initial.contact || "",
    // Parents (new granular fields)
    fatherLast: initial.fatherLast || "",
    fatherFirst: initial.fatherFirst || "",
    fatherMiddle: initial.fatherMiddle || "",
    motherMaidenLast: initial.motherMaidenLast || "",
    motherFirst: initial.motherFirst || "",
    motherMiddle: initial.motherMiddle || "",
    fatherContact: initial.fatherContact || "",
    fatherEmail: initial.fatherEmail || "",
    motherContact: initial.motherContact || "",
    motherEmail: initial.motherEmail || "",
    // Backward compatibility single field
    parentName: initial.parentName || "",
    parentContact: initial.parentContact || "",
    parentEmail: initial.parentEmail || "",
    guardianName: initial.guardianName || "",
    guardianContact: initial.guardianContact || "",
    guardianEmail: initial.guardianEmail || "",
    ipCommunity: initial.ipCommunity || "",
    fourPsId: initial.fourPsId || "",
    previousSchool: initial.previousSchool || "",
    enrollmentStatus: initial.enrollmentStatus || "",
    escProgram: Boolean(initial.escProgram || initial.esc || initial.hasEsc || initial.voucherEsc) || false,
    levelName: initial.levelName || "",
    customDiscountPercent: initial.customDiscountPercent || "",
    applyCustomDiscount: Boolean(initial.applyCustomDiscount) || false,
    paymentMode: initial.paymentMode || "",
    campus: initial.campus || "gapan",
    enrollmentYear: initial.enrollmentYear || new Date().getFullYear()
  });

  useEffect(() => {
    const initFirst = initial.firstName || initial.firstname || initial.first_name || "";
    const initLast = initial.lastName || initial.lastname || initial.last_name || "";
    const initMiddle = initial.middlename || initial.middleName || initial.middle_name || "";
    const initFull = String(initial.fullname || initial.fullName || initial.name || "").trim();
    const parseFromFull = (s) => {
      const t = String(s || "").trim();
      if (!t) return { first: "", middle: "", last: "" };
      if (t.includes(",")) {
        const [lastPart, restPart] = t.split(",", 2).map(x => String(x || "").trim());
        const rest = restPart ? restPart.split(/\s+/).filter(Boolean) : [];
        return { first: rest[0] || "", middle: rest.slice(1).join(" "), last: lastPart };
      }
      const parts = t.split(/\s+/).filter(Boolean);
      if (parts.length === 1) return { first: parts[0], middle: "", last: "" };
      if (parts.length === 2) return { first: parts[0], middle: "", last: parts[1] };
      return { first: parts[0], middle: parts.slice(1, -1).join(" "), last: parts[parts.length - 1] };
    };
    const fromFull = parseFromFull(initFull);
    setForm({
      id: initial.id || initial.studentId || "",
      photo: null,
      photoUrl: initial.photoUrl || "",
      lrn: initial.lrn || "",
      currentHouse: initial.currentHouse || "",
      currentStreet: initial.currentStreet || "",
      currentBarangay: initial.currentBarangay || "",
      currentCity: initial.currentCity || initial.address || "",
      currentProvince: initial.currentProvince || "",
      currentCountry: initial.currentCountry || "Philippines",
      currentZip: initial.currentZip || "",
      permanentHouse: initial.permanentHouse || "",
      permanentStreet: initial.permanentStreet || "",
      permanentBarangay: initial.permanentBarangay || "",
      permanentCity: initial.permanentCity || "",
      permanentProvince: initial.permanentProvince || "",
      permanentCountry: initial.permanentCountry || "Philippines",
      permanentZip: initial.permanentZip || "",
      samePermanentAsCurrent: !!initial.samePermanentAsCurrent,
      email: initial.email || "",
      lastname: String(initLast || fromFull.last || "").trim(),
      firstname: String(initFirst || fromFull.first || "").trim(),
      middlename: String(initMiddle || fromFull.middle || "").trim(),
      gradeLevel: initial.gradeLevel || "",
      strand: initial.strand || "",
      section: initial.section || "",
      birthdate: normalizeDateInput(initial.birthdate || initial.birthDate || initial.birth_date),
      gender: initial.gender || "",
      birthPlaceCity: initial.birthPlaceCity || "",
      motherTongue: initial.motherTongue || "",
      address: initial.address || "",
      contact: initial.contact || "",
      fatherLast: initial.fatherLast || "",
      fatherFirst: initial.fatherFirst || "",
      fatherMiddle: initial.fatherMiddle || "",
      motherMaidenLast: initial.motherMaidenLast || "",
      motherFirst: initial.motherFirst || "",
      motherMiddle: initial.motherMiddle || "",
      fatherContact: initial.fatherContact || "",
      fatherEmail: initial.fatherEmail || "",
      motherContact: initial.motherContact || "",
      motherEmail: initial.motherEmail || "",
      parentName: initial.parentName || "",
      parentContact: initial.parentContact || "",
      parentEmail: initial.parentEmail || "",
      guardianName: initial.guardianName || "",
      guardianContact: initial.guardianContact || "",
      guardianEmail: initial.guardianEmail || "",
      ipCommunity: initial.ipCommunity || "",
      fourPsId: initial.fourPsId || "",
      previousSchool: initial.previousSchool || "",
      enrollmentStatus: initial.enrollmentStatus || "",
      escProgram: Boolean(initial.escProgram || initial.esc || initial.hasEsc || initial.voucherEsc) || false,
      levelName: initial.levelName || "",
      customDiscountPercent: initial.customDiscountPercent || "",
      applyCustomDiscount: Boolean(initial.applyCustomDiscount) || false,
      paymentMode: initial.paymentMode || "",
      campus: initial.campus || "gapan",
      enrollmentYear: initial.enrollmentYear || new Date().getFullYear()
    });
  }, [String(initial?.id || initial?.studentId || "")]);

  const levels = tuitionConfigs[form.campus]?.levels || [];
  const [useCamera, setUseCamera] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameras, setCameras] = useState([]);
  const [cameraId, setCameraId] = useState("");

  async function startCamera() {
    try {
      setCameraError("");
      if (streamRef.current) {
        try { streamRef.current.getTracks().forEach(t => t.stop()); } catch (e) { void e; }
        streamRef.current = null;
      }
      // Enforce HTTPS except localhost
      if (!(location.protocol === "https:" || location.hostname === "localhost")) {
        setCameraError("Camera requires HTTPS (or localhost). Open the portal via https://");
        setUseCamera(false);
        return;
      }
      let constraints = cameraId
        ? { video: { deviceId: { exact: cameraId } }, audio: false }
        : { video: { facingMode: "user" }, audio: false };
      let stream = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err) {
        // Fallback if exact device constraint fails
        if (String(err?.name).includes("OverconstrainedError") || String(err?.name).includes("NotFoundError")) {
          constraints = { video: true, audio: false };
          stream = await navigator.mediaDevices.getUserMedia(constraints);
        } else {
          throw err;
        }
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          videoRef.current.onloadedmetadata = async () => {
            try { await videoRef.current.play(); } catch (e) { void e; }
          };
          await videoRef.current.play();
        } catch (e) { void e; }
      }
      setUseCamera(true);
    } catch (e) {
      setUseCamera(false);
      const name = e?.name || "Error";
      const msg = e?.message || "";
      let hint = "";
      if (name === "NotAllowedError") hint = "Permission denied — allow Camera in the browser/site permissions.";
      else if (name === "NotFoundError") hint = "No camera detected — check macOS Privacy & Security > Camera and cables.";
      else if (name === "NotReadableError") hint = "Camera is in use by another app — close apps using the camera and retry.";
      else if (name === "OverconstrainedError") hint = "Selected camera not available — choose a different device.";
      setCameraError(`${name}: ${msg} ${hint}`.trim());
    }
  }

  function stopCamera() {
    try {
      const s = streamRef.current;
      if (s) s.getTracks().forEach(t => t.stop());
    } catch (e) { void e; }
    streamRef.current = null;
    setUseCamera(false);
  }

  async function capturePhoto() {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob(blob => {
      if (!blob) return;
      const file = new File([blob], "capture.jpg", { type: "image/jpeg" });
      setForm(prev => ({ ...prev, photo: file, photoUrl: URL.createObjectURL(file) }));
      stopCamera();
    }, "image/jpeg", 0.92);
  }
  async function loadCameras() {
    try {
      try {
        if (!streamRef.current) {
          await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
        }
      } catch (e) { void e; }
      const devices = await navigator.mediaDevices.enumerateDevices();
      const vids = devices.filter(d => d.kind === "videoinput");
      setCameras(vids);
      if (!cameraId && vids.length > 0) setCameraId(vids[0].deviceId);
    } catch (e) { void e; }
  }

  const levelNameForGrade = useMemo(() => {
    const g = String(form.gradeLevel || "");
    if (!g) return "";
    const elemGrades = ["Nursery", "Pre-Kinder", "Kinder", "Pre-Elem", "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", "Grade 6"];
    const jhsGrades = ["Grade 7", "Grade 8", "Grade 9", "Grade 10"];
    const shsGrades = ["Grade 11", "Grade 12"];
    if (elemGrades.includes(g)) return "Pre-Elem to Grade 6";
    if (jhsGrades.includes(g)) return form.escProgram ? "Grades 7-10 (With PEAC Subsidy/ESC I.D.)" : "Pre-Elem to Grade 10";
    if (shsGrades.includes(g)) return "Senior High School (no voucher)";
    return g;
  }, [form.gradeLevel, form.escProgram]);
  const selectedLevel = levels.find(l => l.name === levelNameForGrade) || levels[0];
  const paymentModes = selectedLevel?.paymentModes || [];
  const paymentSchedule = form.gradeLevel && form.paymentMode
    ? getStudentPaymentSchedule({
        campus: form.campus,
        levelName: form.levelName || selectedLevel?.name || form.gradeLevel,
        paymentMode: form.paymentMode,
        customDiscount: form.customDiscountPercent ? Number(form.customDiscountPercent) / 100 : null
      })
    : null;

  // Auto-select defaults to show schedule sooner
  useEffect(() => {
    // set default tuition level name if not set
    if (form.gradeLevel && !form.levelName && selectedLevel?.name) {
      setForm(prev => ({ ...prev, levelName: selectedLevel.name }));
    }
    // set default payment mode if not chosen
    if (form.gradeLevel && (!form.paymentMode || form.paymentMode === "") && (selectedLevel?.paymentModes?.length > 0)) {
      setForm(prev => ({ ...prev, paymentMode: selectedLevel.paymentModes[0].mode }));
    }
  }, [form.gradeLevel, form.campus]);

  const handleChange = e => {
    const { name, value, files } = e.target;
    let next;
    if (files) {
      next = files[0];
    } else if (e.target.type === "checkbox") {
      next = e.target.checked;
    } else {
      next = value;
    }
    if (name === "campus") next = String(next).toLowerCase();
    if (name === "samePermanentAsCurrent") {
      const checked = Boolean(next);
      if (checked) {
        setForm(prev => ({
          ...prev,
          samePermanentAsCurrent: true,
          permanentHouse: prev.currentHouse || "",
          permanentStreet: prev.currentStreet || "",
          permanentBarangay: prev.currentBarangay || "",
          permanentCity: prev.currentCity || "",
          permanentProvince: prev.currentProvince || "",
          permanentCountry: prev.currentCountry || "Philippines",
          permanentZip: prev.currentZip || ""
        }));
        return;
      }
    }
    setForm(prev => ({ ...prev, [name]: next }));
  };

  const [gradeSectionsMap, setGradeSectionsMap] = useState({});
  useEffect(() => {
    let active = true;
    (async () => {
      const map = await loadGradeSections(form.campus);
      if (active) setGradeSectionsMap(map || {});
    })();
    return () => { active = false; };
  }, [form.campus]);

  const sectionOptions = useMemo(() => {
    const fromState = gradeSectionsMap[form.gradeLevel];
    if (fromState && fromState.length) return fromState;
    const defaultGapanSections = {
      "Pre-Kinder": ["Nazareth"],
      "Kinder": ["Galilee", "Bethany"],
      "Grade 1": ["Gethsemane", "Zion"],
      "Grade 2": ["Capernaum"],
      "Grade 3": ["Judea"],
      "Grade 4": ["Cyrene"],
      "Grade 5": ["Issachar"],
      "Grade 6": ["Israel", "Hebron"],
      "Grade 7": ["Love"],
      "Grade 8": ["Kindness"],
      "Grade 9": ["Faith"],
      "Grade 10": ["Excellence"],
      "Grade 11": ["Peace"],
      "Grade 12": ["Compassion"]
    };
    try {
      const raw = localStorage.getItem("gradeSections");
      const map = raw ? JSON.parse(raw) : {};
    const campusLC = String(form.campus || "").toLowerCase();
      const campusIsGapan = campusLC === "gapan";
      const fromStorage = (map && map[form.gradeLevel]) ? map[form.gradeLevel] : [];
      if (fromStorage && fromStorage.length > 0) return fromStorage;
      if (campusIsGapan) return defaultGapanSections[form.gradeLevel] || [];
      return [];
    } catch {
      const campusLC = String(form.campus || "").toLowerCase();
      if (campusLC === "gapan") {
        return defaultGapanSections[form.gradeLevel] || [];
      }
      return [];
    }
  }, [form.gradeLevel, form.campus, gradeSectionsMap]);

  async function submit(e) {
    e.preventDefault();
    const trim = (v) => String(v || "").trim();
    const grade = trim(form.gradeLevel);
    const campusLC = trim(form.campus).toLowerCase();
    const userObj = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; } })();
    const userCampus = String(userObj?.campus || "").toLowerCase();
    const userRoles = (userObj?.roles || []);
    const isAdmin = Boolean(userObj?.admin || userObj?.superadmin || (Array.isArray(userRoles) && userRoles.some(r => String(r).toLowerCase().includes("admin"))));
    const birth = normalizeDateInput(trim(form.birthdate));
    const lrn = trim(form.lrn);
    if (!grade) { alert("Grade Level is required."); return; }
    if (!campusLC) { alert("Campus is required."); return; }
    if (!trim(form.lastname) || !trim(form.firstname)) { alert("Last Name and First Name are required."); return; }
    if (!birth) { alert("Birthdate is required."); return; }
    if (!trim(form.enrollmentStatus)) { alert("Student Status (New/Old) is required."); return; }
    if (!trim(form.levelName || selectedLevel?.name || "")) { alert("Tuition Level is required."); return; }
    if (!trim(form.paymentMode)) { alert("Mode of Payment is required."); return; }
    const emailNorm = (v) => {
      const s = trim(v).toLowerCase();
      return s && s.includes("@") ? s : "";
    };
    const middleName = trim(form.middlename);
    const fullname = `${trim(form.firstname)} ${middleName ? middleName + " " : ""}${trim(form.lastname)}`.trim();
    const addressStr = trim(form.currentCity);
    const normalizedGender = (() => {
      const g = trim(form.gender).toLowerCase();
      if (g.startsWith("m")) return "male";
      if (g.startsWith("f")) return "female";
      return "";
    })();
    const sectionsArr = (() => {
      const raw = trim(form.section);
      if (!raw) return [];
      if (raw.includes(",")) return raw.split(",").map(s => s.trim()).filter(Boolean);
      return [raw];
    })();
    const record = {
      id: trim(form.id),
      lrn,
      firstName: trim(form.firstname),
      middleName,
      lastName: trim(form.lastname),
      firstNameLower: trim(form.firstname).toLowerCase(),
      lastNameLower: trim(form.lastname).toLowerCase(),
      fullname,
      gradeLevel: grade,
      section: sectionsArr[0] || "",
      sections: sectionsArr,
      strand: ((grade || "").includes("Grade 11") || (grade || "").includes("Grade 12")) ? (sectionsArr[0] || "") : "",
      campus: campusLC,
      gender: normalizedGender,
      birthdate: birth,
      birthPlaceCity: trim(form.birthPlaceCity),
      motherTongue: trim(form.motherTongue),
      address: addressStr,
      currentHouse: trim(form.currentHouse),
      currentStreet: trim(form.currentStreet),
      currentBarangay: trim(form.currentBarangay),
      currentCity: trim(form.currentCity),
      currentProvince: trim(form.currentProvince),
      currentCountry: trim(form.currentCountry) || "Philippines",
      currentZip: trim(form.currentZip),
      permanentHouse: trim(form.permanentHouse),
      permanentStreet: trim(form.permanentStreet),
      permanentBarangay: trim(form.permanentBarangay),
      permanentCity: trim(form.permanentCity),
      permanentProvince: trim(form.permanentProvince),
      permanentCountry: trim(form.permanentCountry) || "Philippines",
      permanentZip: trim(form.permanentZip),
      samePermanentAsCurrent: !!form.samePermanentAsCurrent,
      contact: trim(form.contact),
      email: emailNorm(form.email),
      fatherLast: trim(form.fatherLast),
      fatherFirst: trim(form.fatherFirst),
      fatherMiddle: trim(form.fatherMiddle),
      motherMaidenLast: trim(form.motherMaidenLast),
      motherFirst: trim(form.motherFirst),
      motherMiddle: trim(form.motherMiddle),
      fatherContact: trim(form.fatherContact),
      fatherEmail: emailNorm(form.fatherEmail),
      motherContact: trim(form.motherContact),
      motherEmail: emailNorm(form.motherEmail),
      parentName: trim(form.parentName),
      parentContact: trim(form.parentContact) || trim(form.fatherContact) || trim(form.motherContact),
      parentEmail: emailNorm(form.parentEmail) || emailNorm(form.fatherEmail) || emailNorm(form.motherEmail),
      guardianName: trim(form.guardianName),
      guardianContact: trim(form.guardianContact),
      guardianEmail: emailNorm(form.guardianEmail),
      ipCommunity: trim(form.ipCommunity),
      fourPsId: trim(form.fourPsId),
      previousSchool: trim(form.previousSchool),
      enrollmentStatus: trim(form.enrollmentStatus),
      escProgram: Boolean(form.escProgram),
      levelName: trim(form.levelName || selectedLevel?.name || ""),
      applyCustomDiscount: Boolean(form.applyCustomDiscount) || (Number(form.customDiscountPercent || 0) > 0),
      customDiscountPercent: Number(form.customDiscountPercent || 0) || 0,
      paymentMode: form.paymentMode || "",
      paymentSchedule: paymentSchedule || null,
      enrollmentYear: Number(form.enrollmentYear || new Date().getFullYear()),
      photoUrl: form.photoUrl || "",
      updatedAt: new Date().toISOString(),
      updatedBy: (JSON.parse(localStorage.getItem("user") || "{}")?.email) || "system"
    };
    // Enforce campus for non-admin to avoid Firestore rule rejection
    if (!isAdmin && !userCampus) {
      alert("Your account is missing a campus assignment. The record will be saved as entered; if it fails, please ask admin to set your campus claim.");
    }
    if (!isAdmin && userCampus && record.campus !== userCampus) {
      alert(`You are signed in for campus "${userCampus}". The record will be saved to that campus.`);
      record.campus = userCampus;
    }
    if (form.photo && record.id) {
      try {
        const url = await uploadStudentPhoto(record.id, form.photo);
        onSubmit({ ...record, photoUrl: url });
        return;
      } catch {
        onSubmit(record);
        return;
      }
    }
    if (form.photo && !record.id) {
      const reader = new FileReader();
      reader.onload = () => onSubmit({ ...record, photoUrl: reader.result });
      reader.onerror = () => onSubmit(record);
      reader.readAsDataURL(form.photo);
      return;
    }
    onSubmit(record);
  }

  return (
    <form onSubmit={submit}>
      <div className="row">
        <div className="col-md-3 text-center mb-3">
          <label htmlFor="photo">
            <img
              src={form.photo ? URL.createObjectURL(form.photo) : (form.photoUrl || "/vmsc_logo.png")}
              alt="Student"
              className="img-fluid rounded mb-2"
              style={{ maxHeight: 150, cursor: "pointer" }}
            />
          </label>
          <input type="file" id="photo" name="photo" accept="image/*" capture="user" className="form-control" onChange={handleChange} />
          {!useCamera && (
            <button type="button" className="btn btn-outline-primary btn-sm mt-2" onClick={() => { loadCameras(); startCamera(); }}>
              Use Camera
            </button>
          )}
          {useCamera && (
            <div className="mt-2">
              <div className="d-flex gap-2 mb-2">
                <select className="form-select form-select-sm" value={cameraId} onChange={(e) => { setCameraId(e.target.value); stopCamera(); startCamera(); }}>
                  {cameras.map(cam => (<option key={cam.deviceId} value={cam.deviceId}>{cam.label || "Camera"}</option>))}
                </select>
              </div>
              <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", maxHeight: 180, background: "#000" }} />
              <div className="d-flex gap-2 mt-2">
                <button type="button" className="btn btn-primary btn-sm" onClick={capturePhoto}>Capture</button>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={stopCamera}>Close Camera</button>
              </div>
              {cameraError && <div className="text-danger small mt-2">{cameraError}</div>}
            </div>
          )}
        </div>
        <div className="col-md-9">
          <div className="row">
            <div className="col-md-3 mb-3">
              <label>Campus</label>
              <select name="campus" className="form-select" value={form.campus} onChange={handleChange} required>
                <option value="gapan">Gapan</option>
                <option value="bulacan">Bulacan</option>
              </select>
            </div>
            <div className="col-md-3 mb-3">
              <label>Grade Level</label>
              <select name="gradeLevel" className="form-select" value={form.gradeLevel} onChange={handleChange} required>
                <option value="">Select Grade</option>
                <option>Nursery</option>
                <option>Pre-Kinder</option>
                <option>Kinder</option>
                <option>Grade 1</option>
                <option>Grade 2</option>
                <option>Grade 3</option>
                <option>Grade 4</option>
                <option>Grade 5</option>
                <option>Grade 6</option>
                <option>Grade 7</option>
                <option>Grade 8</option>
                <option>Grade 9</option>
                <option>Grade 10</option>
                <option>Grade 11</option>
                <option>Grade 12</option>
              </select>
            </div>
            <div className="col-md-3 mb-3">
              <label>Student Status</label>
              <select name="enrollmentStatus" className="form-select" value={form.enrollmentStatus} onChange={handleChange} required>
                <option value="">Select Status</option>
                <option value="New">New Student</option>
                <option value="Continuing">Old Student (Returning)</option>
              </select>
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">ESC Program (Grades 7–10)</label>
              <div className="form-check">
                <input className="form-check-input" type="checkbox" id="escProgram" name="escProgram" checked={!!form.escProgram} onChange={handleChange} />
                <label className="form-check-label" htmlFor="escProgram">Apply ESC subsidy</label>
              </div>
            </div>
            <div className="col-md-4 mb-3">
              <label>Tuition Level</label>
              <select
                name="levelName"
                className="form-select"
                value={form.levelName || ""}
                onChange={handleChange}
                required
              >
                <option value="">{selectedLevel?.name || "Select Level"}</option>
                {(tuitionConfigs[form.campus]?.levels || []).map(l => (
                  <option key={l.name} value={l.name}>{l.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4 mb-3">
              <label>Section</label>
              <select name="section" className="form-select" value={form.section} onChange={handleChange}>
                <option value="">Select Section</option>
                {sectionOptions.map(sec => <option key={sec} value={sec}>{sec}</option>)}
              </select>
            </div>
            <div className="col-md-4 mb-3">
              <label>LRN</label>
              <input type="text" name="lrn" className="form-control" value={form.lrn} onChange={handleChange} />
            </div>
            <div className="col-md-4 mb-3">
              <label>Last Name</label>
              <input type="text" name="lastname" className="form-control" value={form.lastname} onChange={handleChange} />
            </div>
            <div className="col-md-4 mb-3">
              <label>First Name</label>
              <input type="text" name="firstname" className="form-control" value={form.firstname} onChange={handleChange} />
            </div>
            <div className="col-md-4 mb-3">
              <label>Middle Name</label>
              <input type="text" name="middlename" className="form-control" value={form.middlename} onChange={handleChange} />
            </div>
            <div className="col-md-4 mb-3">
              <label>Gender</label>
              <select name="gender" className="form-select" value={form.gender} onChange={handleChange}>
                <option value="">Select Gender</option>
                <option>Male</option>
                <option>Female</option>
              </select>
            </div>
            <div className="col-md-4 mb-3">
              <label>Birthdate</label>
              <input type="date" name="birthdate" className="form-control" value={form.birthdate} onChange={handleChange} required />
            </div>
            <div className="col-md-4 mb-3">
              <label>Place of Birth (Municipality/City)</label>
              <input type="text" name="birthPlaceCity" className="form-control" value={form.birthPlaceCity} onChange={handleChange} />
            </div>
            <div className="col-12">
              <h6 className="mt-2">Current Address</h6>
            </div>
            <div className="col-md-2 mb-3">
              <label>House No.</label>
              <input type="text" name="currentHouse" className="form-control" value={form.currentHouse} onChange={handleChange} />
            </div>
            <div className="col-md-3 mb-3">
              <label>Sitio/Street Name</label>
              <input type="text" name="currentStreet" className="form-control" value={form.currentStreet} onChange={handleChange} />
            </div>
            <div className="col-md-2 mb-3">
              <label>Barangay</label>
              <input type="text" name="currentBarangay" className="form-control" value={form.currentBarangay} onChange={handleChange} />
            </div>
            <div className="col-md-3 mb-3">
              <label>Municipality/City</label>
              <input type="text" name="currentCity" className="form-control" value={form.currentCity} onChange={handleChange} />
            </div>
            <div className="col-md-2 mb-3">
              <label>Province</label>
              <input type="text" name="currentProvince" className="form-control" value={form.currentProvince} onChange={handleChange} />
            </div>
            <div className="col-md-2 mb-3">
              <label>Country</label>
              <input type="text" name="currentCountry" className="form-control" value={form.currentCountry} onChange={handleChange} />
            </div>
            <div className="col-md-2 mb-3">
              <label>Zip Code</label>
              <input type="text" name="currentZip" className="form-control" value={form.currentZip} onChange={handleChange} />
            </div>
            <div className="col-12">
              <h6 className="mt-2">Permanent Address</h6>
            </div>
            <div className="col-12 mb-2">
              <div className="form-check">
                <input className="form-check-input" type="checkbox" id="samePermanentAsCurrent" name="samePermanentAsCurrent" checked={!!form.samePermanentAsCurrent} onChange={handleChange} />
                <label className="form-check-label" htmlFor="samePermanentAsCurrent">Same with Current Address</label>
              </div>
            </div>
            <div className="col-md-2 mb-3">
              <label>House No./Street</label>
              <input type="text" name="permanentHouse" className="form-control" value={form.permanentHouse} onChange={handleChange} />
            </div>
            <div className="col-md-3 mb-3">
              <label>Street Name</label>
              <input type="text" name="permanentStreet" className="form-control" value={form.permanentStreet} onChange={handleChange} />
            </div>
            <div className="col-md-2 mb-3">
              <label>Barangay</label>
              <input type="text" name="permanentBarangay" className="form-control" value={form.permanentBarangay} onChange={handleChange} />
            </div>
            <div className="col-md-3 mb-3">
              <label>Municipality/City</label>
              <input type="text" name="permanentCity" className="form-control" value={form.permanentCity} onChange={handleChange} />
            </div>
            <div className="col-md-2 mb-3">
              <label>Province</label>
              <input type="text" name="permanentProvince" className="form-control" value={form.permanentProvince} onChange={handleChange} />
            </div>
            <div className="col-md-2 mb-3">
              <label>Country</label>
              <input type="text" name="permanentCountry" className="form-control" value={form.permanentCountry} onChange={handleChange} />
            </div>
            <div className="col-md-2 mb-3">
              <label>Zip Code</label>
              <input type="text" name="permanentZip" className="form-control" value={form.permanentZip} onChange={handleChange} />
            </div>
            <div className="col-md-4 mb-3">
              <label>Contact Number</label>
              <input type="text" name="contact" className="form-control" value={form.contact} onChange={handleChange} />
            </div>
            <div className="col-md-4 mb-3">
              <label>Student Email</label>
              <input type="email" name="email" className="form-control" value={form.email} onChange={handleChange} placeholder="e.g., student@vmsc.education" />
            </div>
            <div className="col-md-4 mb-3">
              <label>Mother Tongue</label>
              <input type="text" name="motherTongue" className="form-control" value={form.motherTongue} onChange={handleChange} />
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-3 mb-3">
          <label>Mode of Payment</label>
          <select name="paymentMode" className="form-select" value={form.paymentMode} onChange={handleChange} required>
            <option value="">Select Mode</option>
            {paymentModes.map(pm => <option key={pm.mode} value={pm.mode}>{pm.mode}</option>)}
          </select>
        </div>
        <div className="col-md-3 mb-3">
          <label>Custom Discount % (optional)</label>
          <input type="number" name="customDiscountPercent" className="form-control" value={form.customDiscountPercent || ""} onChange={handleChange} min="0" max="100" step="0.01" />
        </div>
        <div className="col-md-3 mb-3">
          <label className="form-label">Apply Custom Discount</label>
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="applyCustomDiscount" name="applyCustomDiscount" checked={!!form.applyCustomDiscount} onChange={handleChange} />
            <label className="form-check-label" htmlFor="applyCustomDiscount">Use discount in tuition computation</label>
          </div>
        </div>
        <div className="col-md-6 mb-3">
          <label>IP Community (If applicable)</label>
          <input type="text" name="ipCommunity" className="form-control" value={form.ipCommunity} onChange={handleChange} />
        </div>
        <div className="col-md-6 mb-3">
          <label>4Ps Household ID (If applicable)</label>
          <input type="text" name="fourPsId" className="form-control" value={form.fourPsId} onChange={handleChange} />
        </div>
      </div>
      <h5 className="mt-4">Parent / Guardian Information</h5>
      <div className="row">
        <div className="col-md-12">
          <div className="row">
            <div className="col-md-4 mb-3">
              <label>Father's Last Name</label>
              <input type="text" name="fatherLast" className="form-control" value={form.fatherLast} onChange={handleChange} />
            </div>
            <div className="col-md-4 mb-3">
              <label>Father's First Name</label>
              <input type="text" name="fatherFirst" className="form-control" value={form.fatherFirst} onChange={handleChange} />
            </div>
            <div className="col-md-4 mb-3">
              <label>Father's Middle Name</label>
              <input type="text" name="fatherMiddle" className="form-control" value={form.fatherMiddle} onChange={handleChange} />
            </div>
          </div>
          <div className="row">
            <div className="col-md-4 mb-3">
              <label>Mother's Maiden Last Name</label>
              <input type="text" name="motherMaidenLast" className="form-control" value={form.motherMaidenLast} onChange={handleChange} />
            </div>
            <div className="col-md-4 mb-3">
              <label>Mother's First Name</label>
              <input type="text" name="motherFirst" className="form-control" value={form.motherFirst} onChange={handleChange} />
            </div>
            <div className="col-md-4 mb-3">
              <label>Mother's Middle Name</label>
              <input type="text" name="motherMiddle" className="form-control" value={form.motherMiddle} onChange={handleChange} />
            </div>
          </div>
        </div>
        <div className="col-md-6 mb-3">
          <label>Father's Contact</label>
          <input type="text" name="fatherContact" className="form-control" value={form.fatherContact} onChange={handleChange} />
        </div>
        <div className="col-md-6 mb-3">
          <label>Father's Email</label>
          <input type="email" name="fatherEmail" className="form-control" value={form.fatherEmail} onChange={handleChange} />
        </div>
        <div className="col-md-6 mb-3">
          <label>Mother's Contact</label>
          <input type="text" name="motherContact" className="form-control" value={form.motherContact} onChange={handleChange} />
        </div>
        <div className="col-md-6 mb-3">
          <label>Mother's Email</label>
          <input type="email" name="motherEmail" className="form-control" value={form.motherEmail} onChange={handleChange} />
        </div>
        {/* Backward compatibility fields (hidden) */}
        <input type="hidden" name="parentContact" value={form.parentContact} readOnly />
        <input type="hidden" name="parentEmail" value={form.parentEmail} readOnly />
        <div className="col-md-6 mb-3">
          <label>Guardian's Name</label>
          <input type="text" name="guardianName" className="form-control" value={form.guardianName} onChange={handleChange} />
        </div>
        <div className="col-md-6 mb-3">
          <label>Guardian's Contact</label>
          <input type="text" name="guardianContact" className="form-control" value={form.guardianContact} onChange={handleChange} />
        </div>
        <div className="col-md-6 mb-3">
          <label>Guardian's Email</label>
          <input type="email" name="guardianEmail" className="form-control" value={form.guardianEmail} onChange={handleChange} />
        </div>
      </div>
      <h5 className="mt-4">Previous School</h5>
      <div className="row">
        <div className="col-md-8 mb-3">
          <label>Previous School Name</label>
          <input type="text" name="previousSchool" className="form-control" value={form.previousSchool} onChange={handleChange} />
        </div>
        {paymentSchedule && (
          <div className="col-md-8 mb-3">
            <ul className="list-group">
              {paymentSchedule.schedule.map((sch, idx) => (
                <li key={idx} className="list-group-item">
                  {sch.label}: Tuition - ₱{sch.tuition || 0}, Misc - ₱{sch.misc || 0}, Total - ₱{sch.total || 0}
                </li>
              ))}
              <li className="list-group-item fw-bold">Total Due: ₱{paymentSchedule.totalDue}</li>
            </ul>
          </div>
        )}
        {!paymentSchedule && (
          <div className="col-md-8 mb-3 text-muted small">
            Select Grade Level, Tuition Level and Mode of Payment above to preview the tuition schedule and total due.
          </div>
        )}
      </div>
      <div className="mt-3 d-flex justify-content-end gap-2">
        {onCancel && <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>}
        <button className="btn btn-success">Save</button>
      </div>
    </form>
  );
}
